import { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router';
import { Search, ChevronDown, MoreVertical, Pencil, Trash2, MapPin, FileText, FileCheck, Mail, Check, CheckCheck, AlertTriangle, ArrowUp, ArrowDown, Columns3, SlidersHorizontal, X, Archive, ArchiveRestore } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { CustomerManagement, CustomerListCard } from '../components/CustomerManagement';
import { CustomerDetailPanel } from '../components/customer-detail/CustomerDetailPanel';
import type { Customer } from '../components/CustomerManagement';
import { DataRequestModal } from '../components/DataRequestModal';
import { api } from '../lib/api';
import { API_BASE } from '../lib/apiBase';
import { fehlendeLabels } from '../lib/pflichtfelder';
import { CustomerLogo } from '../components/CustomerLogo';
import { HilfeLink } from '../components/HilfeLink';

interface ApiReport {
  id: number;
  status: string;
  customerId: number;
}

interface ApiVorgang {
  id: number;
  customerId: number;
}

interface CustomerRow {
  id: number;
  name: string;
  ansprechpartner: string;
  ort: string;
  website: string;
  wirtschaftszweig: string;
  berichteCount: number;
  status: 'Aktiv' | 'Inaktiv' | 'Neu';
  letzteAktualisierungDatum: string;
  letzteAktualisierungZeit: string;
  letzteAktualisierungRaw: string;
  lastEventLabel: string;
  lastEventVorgangsnummer: string;
  lastAntragDatum: string;
  /** Noch laufender Vorgang (Nummer + Startdatum) — Grundlage für die Rückfrage. */
  offenerVorgang: { vorgangsnummer: string; seit: string } | null;
  antragCountThisYear: number;
  invitationStatus?: 'pending' | 'completed' | null;
  /** Beschriftungen der noch leeren Pflichtfelder (Reihenfolge wie im Editor). */
  fehlendeFelder: string[];
  logoUrl?: string;
  vnReady?: boolean;
  hasDocuments?: boolean;
  foerderfaehigkeitAmpel?: string | null;
  archived?: boolean;
}

/** Event-Typ → Kurzlabel (max. 2 Wörter) für die Anzeige in der Status-Spalte. */
const EVENT_SHORT: Record<string, string> = {
  antrag_gestellt: 'Antrag gestellt',
  antrag_gestartet: 'Antrag geöffnet',
  antrag_hinzugefuegt: 'Antrag eingegangen',
  erlaubnis_erteilt: 'Erlaubnis erhalten',
  bescheid_hinzugefuegt: 'Bescheid erhalten',
  verwendungsnachweis_hinzugefuegt: 'VN-Vorlage',
  dokument_hinzugefuegt: 'Dokument eingegangen',
  nachforderung_eingegangen: 'Nachforderung',
  ablehnung_eingegangen: 'Ablehnung',
  auszahlung_bestaetigt: 'Auszahlung erfolgt',
  frist_verlaengert: 'Frist verlängert',
  mahnung_eingegangen: 'Mahnung',
  bericht_erstellt: 'Bericht erstellt',
  bericht_zur_unterschrift: 'Bericht zur US',
  bericht_unterschrieben: 'Bericht unterschrieben',
  verwendungsnachweis_erstellt: 'VN erstellt',
  verwendungsnachweis_zur_unterschrift: 'VN zur US',
  verwendungsnachweis_unterschrieben: 'VN unterschrieben',
  verwendungsnachweis_hochgeladen: 'VN hochgeladen',
  vollmacht_hochgeladen: 'Vollmacht hochgel.',
  grc_hochgeladen: 'GRC hochgeladen',
  deminimis_hochgeladen: 'De-Minimis hochgel.',
  rechnung_hochgeladen: 'Rechnung hochgel.',
  kontoauszug_hochgeladen: 'Kontoauszug hochgel.',
  dokument_geloescht: 'Dokument gelöscht',
  dokument_wiederhergestellt: 'wiederhergestellt',
};

/** Gründe, warum "ANTRAG STELLEN" vor dem Absenden eine Rückfrage zeigen sollte. */
export function antragWarningReasons(row: CustomerRow, year: number): string[] {
  const reasons: string[] = [];
  if (row.antragCountThisYear >= 2) reasons.push(`Bereits ${row.antragCountThisYear}/2 Anträge in ${year} gestellt.`);
  // Ein zweiter Antrag neben einem laufenden ist erlaubt, aber selten gewollt — meistens
  // ist es ein Versehen, weil der erste Vorgang schon Wochen zurückliegt.
  if (row.offenerVorgang) {
    reasons.push(
      `Vorgang ${row.offenerVorgang.vorgangsnummer} vom ${fmtKurzdatum(row.offenerVorgang.seit)} ist noch offen.`,
    );
  }
  if (!row.hasDocuments) reasons.push('Es liegen noch keine Dokumente für diesen Kunden vor.');
  return reasons;
}

/** ISO oder "YYYY-MM-DD" → "16.07.2026". Leerer Text, wenn unlesbar. */
function fmtKurzdatum(wert: string): string {
  const d = new Date(wert);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Tooltip-Text: welche Felder fehlen, gekürzt auf eine lesbare Länge. */
function fehlendText(fehlend: string[], max = 6): string {
  const sichtbar = fehlend.slice(0, max).join(', ');
  const rest = fehlend.length - max;
  return rest > 0 ? `${sichtbar} und ${rest} weitere` : sichtbar;
}

/**
 * Stammdaten-Stand als Beisatz zum Kundennamen (zweite Zeile, neben der Ansprechperson):
 * nicht der Prozentwert, sondern die offene Arbeit — wie viele Pflichtfelder noch fehlen.
 * Vollständige Kunden bekommen nur ein Häkchen und ziehen so keine Aufmerksamkeit.
 * Klick öffnet direkt die Stammdaten.
 */
function DatenStatus({ fehlend, inv, onEdit }: {
  fehlend: string[];
  inv?: 'pending' | 'completed' | null;
  onEdit: () => void;
}) {
  if (fehlend.length === 0) {
    const label = inv === 'completed' ? 'Daten vom Kunden eingegangen — vollständig' : 'Alle Pflichtfelder ausgefüllt';
    // Tailwind-Grün wirkt in v4 türkisstichig — daher ein festes, sattes Grün.
    return (
      <span
        className="inline-flex items-center justify-center w-5 h-5 rounded-md shrink-0 bg-[#e6f4ea] dark:bg-[#1a7f37]/25"
        title={label}
        aria-label={label}
      >
        <Check className="w-3.5 h-3.5 text-[#1a7f37] dark:text-[#3fb950]" strokeWidth={2.5} />
      </span>
    );
  }
  const angefordert = inv === 'pending';
  const label = `${angefordert ? 'Daten angefordert — noch ' : ''}${fehlend.length} ${fehlend.length === 1 ? 'Pflichtfeld fehlt' : 'Pflichtfelder fehlen'}: ${fehlendText(fehlend, 99)}`;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onEdit(); }}
      title={label}
      aria-label={label}
      className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[13px] font-medium whitespace-nowrap transition-colors ${
        angefordert
          ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/50'
          : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50'
      }`}
    >
      {angefordert ? <Mail className="w-3 h-3 shrink-0" /> : <AlertTriangle className="w-3 h-3 shrink-0" />}
      <span translate="no" style={{ fontVariantNumeric: 'tabular-nums' }}>{fehlend.length}</span>
      <span>{fehlend.length === 1 ? 'fehlt' : 'fehlen'}</span>
    </button>
  );
}

export default function Customers() {
  const { darkMode } = useDarkMode();
  const currentYear = new Date().getFullYear();
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [triggerNewCustomer, setTriggerNewCustomer] = useState(0);
  const [triggerEditCustomer, setTriggerEditCustomer] = useState<{id: number, ts: number, fokusLuecke?: boolean} | null>(null);

  // Table state
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [fullCustomers, setFullCustomers] = useState<Customer[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  // Archivierte Kunden standardmäßig ausgeblendet — abgesprungene Kunden sollen nicht im
  // Alltag stören, bleiben aber per Häkchen erreichbar (gleiches Muster wie Reports.tsx).
  const [showArchived, setShowArchived] = useState<boolean>(() => {
    try { return localStorage.getItem('customers.showArchived') === 'true'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('customers.showArchived', String(showArchived)); } catch { /* ignore */ }
  }, [showArchived]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
    if (typeof window === 'undefined') return 10;
    const stored = parseInt(localStorage.getItem('customers.rowsPerPage') || '', 10);
    return Number.isFinite(stored) && stored > 0 ? stored : 10;
  });
  useEffect(() => {
    try { localStorage.setItem('customers.rowsPerPage', String(rowsPerPage)); } catch { /* ignorieren */ }
  }, [rowsPerPage]);
  const [columnsDropdownOpen, setColumnsDropdownOpen] = useState(false);
  const [rowsDropdownOpen, setRowsDropdownOpen] = useState(false);

  const SORT_OPTIONS: Array<{ key: string; label: string }> = [
    { key: 'name', label: 'Kundenname' },
    { key: 'ansprechpartner', label: 'Ansprechperson' },
    { key: 'ort', label: 'Ort' },
    { key: 'wirtschaftszweig', label: 'Branche' },
    { key: 'berichteCount', label: 'Vorgänge' },
    { key: 'status', label: 'Status' },
    { key: 'letzteAktualisierung', label: 'Letzte Änderung' },
  ];
  // Sortierung — vordefiniert nach Kundenname (A→Z); zuletzt gewählte Sortierung
  // wird in localStorage gemerkt.
  const [sortColumn, setSortColumn] = useState<string>(() => {
    if (typeof window === 'undefined') return 'name';
    const stored = localStorage.getItem('customers.sortColumn');
    return stored && SORT_OPTIONS.some((o) => o.key === stored) ? stored : 'name';
  });
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    if (typeof window === 'undefined') return 'asc';
    return localStorage.getItem('customers.sortDirection') === 'desc' ? 'desc' : 'asc';
  });
  // Mobil gibt es keine Kopfzeile zum Sortieren — dort sammelt ein Vollbild-Dialog
  // Suche, Sortierung und Spalten in einer Ziehharmonika.
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [offenerBereich, setOffenerBereich] = useState<'sortierung' | 'spalten' | null>('sortierung');
  useEffect(() => {
    try {
      localStorage.setItem('customers.sortColumn', sortColumn);
      localStorage.setItem('customers.sortDirection', sortDirection);
    } catch { /* ignorieren */ }
  }, [sortColumn, sortDirection]);
  const setSort = (key: string) => {
    if (key === sortColumn) setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortColumn(key); setSortDirection('asc'); }
  };
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const [menuBtnEl, setMenuBtnEl] = useState<HTMLButtonElement | null>(null);
  const closeMenuTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelMenuClose = () => { if (closeMenuTimer.current) { clearTimeout(closeMenuTimer.current); closeMenuTimer.current = null; } };
  const scheduleMenuClose = () => { cancelMenuClose(); closeMenuTimer.current = setTimeout(() => setOpenMenuId(null), 800); };
  const openMenuAt = (rowId: number, ev: React.MouseEvent<HTMLButtonElement>) => {
    cancelMenuClose();
    if (openMenuId === rowId) { setOpenMenuId(null); setMenuBtnEl(null); return; }
    const btn = ev.currentTarget;
    const rect = btn.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setMenuBtnEl(btn);
    setOpenMenuId(rowId);
  };
  // Position bei Scroll/Resize aktualisieren — Menü scrollt mit dem Button mit.
  useEffect(() => {
    if (openMenuId === null || !menuBtnEl) return;
    const update = () => {
      const rect = menuBtnEl.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [openMenuId, menuBtnEl]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [antragConfirm, setAntragConfirm] = useState<{ customerId: number; reasons: string[] } | null>(null);
  const [dataRequestCustomer, setDataRequestCustomer] = useState<{ id: number; email: string } | null>(null);
  const [newDataNotice, setNewDataNotice] = useState<string | null>(null);
  const lastStatusMapRef = useRef<Record<number, 'pending' | 'completed'>>({});

  // Reihenfolge der hauptspaltenfähigen Spalten (Reihenfolge im UI = im Dropdown
  // änderbar; ansprechpartner ist Sub-Toggle unter „Name" und wird nicht
  // mitgereiht).
  const DEFAULT_COLUMN_ORDER = ['name', 'ort', 'wirtschaftszweig', 'berichteCount', 'status', 'letzteAktualisierung'];
  const DEFAULT_VISIBLE = {
    name: true,
    logo: true,
    ansprechpartner: true,
    ort: true,
    wirtschaftszweig: false,
    berichteCount: true,
    status: true,
    letzteAktualisierung: true,
  };

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return DEFAULT_VISIBLE;
    try {
      const raw = localStorage.getItem('customers.visibleColumns');
      if (raw) return { ...DEFAULT_VISIBLE, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return DEFAULT_VISIBLE;
  });

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    if (typeof window === 'undefined') return DEFAULT_COLUMN_ORDER;
    try {
      const raw = localStorage.getItem('customers.columnOrder');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          // Nur bekannte Keys übernehmen, fehlende anhängen.
          const known = parsed.filter((k: any) => DEFAULT_COLUMN_ORDER.includes(k));
          const missing = DEFAULT_COLUMN_ORDER.filter(k => !known.includes(k));
          return [...known, ...missing];
        }
      }
    } catch { /* ignore */ }
    return DEFAULT_COLUMN_ORDER;
  });

  useEffect(() => {
    try { localStorage.setItem('customers.visibleColumns', JSON.stringify(visibleColumns)); } catch { /* ignore */ }
  }, [visibleColumns]);
  useEffect(() => {
    try { localStorage.setItem('customers.columnOrder', JSON.stringify(columnOrder)); } catch { /* ignore */ }
  }, [columnOrder]);

  // Schmaler Tabellen-Bereich (768-1069px): "Letzte Änderung" ausblenden und
  // "Vorgänge" unter "Ort" zusammenfassen statt eigener Spalte, damit die
  // Tabelle ohne horizontales Scrollen passt.
  const [narrowDesktop, setNarrowDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px) and (max-width: 1069px)');
    const update = () => setNarrowDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const isColumnVisible = (key: string) =>
    !!visibleColumns[key] && !(narrowDesktop && (key === 'berichteCount' || key === 'letzteAktualisierung'));

  // Noch schmaler (768-991px): Favicon ausblenden, ANTRAG STELLEN auf ein Wort kürzen.
  const [veryNarrowDesktop, setVeryNarrowDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px) and (max-width: 991px)');
    const update = () => setVeryNarrowDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Mobile-Kartenliste, breiterer Bereich (575-767px): Ort/Vorgänge wandern rechts
  // (wo sonst der Chevron war), Anträge-Badge und BEANTRAGEN-Button auf eine Zeile.
  const [mediumMobile, setMediumMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 575px) and (max-width: 767px)');
    const update = () => setMediumMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const moveColumn = (key: string, dir: -1 | 1) => {
    setColumnOrder(order => {
      const idx = order.indexOf(key);
      const newIdx = idx + dir;
      if (idx < 0 || newIdx < 0 || newIdx >= order.length) return order;
      const next = [...order];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };

  // Drag-and-Drop für die Spaltenreihenfolge im Dropdown.
  const [dragKey, setDragKey] = useState<string | null>(null);
  const reorderTo = (from: string, to: string) => {
    if (from === to) return;
    setColumnOrder(order => {
      const fromIdx = order.indexOf(from);
      const toIdx = order.indexOf(to);
      if (fromIdx < 0 || toIdx < 0) return order;
      const next = [...order];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, from);
      return next;
    });
  };

  const columnLabels: Record<string, string> = {
    name: 'Kundenname',
    logo: 'Firmenlogo',
    ansprechpartner: 'Ansprechperson',
    ort: 'Ort',
    wirtschaftszweig: 'Branche',
    berichteCount: 'Vorgänge',
    status: 'Status',
    letzteAktualisierung: 'Letzte Änderung',
  };

  /** Breite pro Spalte fürs grid-template (passend zu den w-X Klassen vorher). */
  const columnWidth = (key: string, ortIsFlex: boolean): string => {
    switch (key) {
      // In rem, damit die Spalten der angehobenen Schriftskala folgen — die Werte sind
      // mit ihr mitgewachsen, sonst würden Namen und Status früher abgeschnitten.
      case 'name': return '19rem';
      case 'ort': return ortIsFlex ? '1fr' : '9rem';
      case 'wirtschaftszweig': return '1fr';
      case 'berichteCount': return '5.5rem';
      case 'status': return '12.5rem';
      case 'letzteAktualisierung': return '9rem';
      default: return 'auto';
    }
  };

  // Auto-open new customer modal when navigated with ?new=1
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setTriggerNewCustomer(prev => prev + 1);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Geteilter Deep-Link auf einen Vorgang (?customer=…&vorgang=…): den Kunden inline
  // aufklappen. Der VorgaengeTab im Panel liest den ?vorgang=-Parameter selbst und
  // klappt den richtigen Vorgang auf + scrollt hin. Nur einmal beim Aufruf anwenden;
  // den ?vorgang= bewusst NICHT entfernen, damit das Panel ihn noch lesen kann.
  // Robust: aktive Suche zurücksetzen und den Ziel-Kunden erzwungen einblenden
  // (deepLinkCustomerId), damit er nicht durch Filter/rowsPerPage-Grenze verborgen bleibt.
  const deepLinkAppliedRef = useRef(false);
  const [deepLinkCustomerId, setDeepLinkCustomerId] = useState<number | null>(null);
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    const cid = Number(searchParams.get('customer'));
    if (cid) {
      setSearchQuery('');
      setExpandedId(cid);
      setDeepLinkCustomerId(cid);
      deepLinkAppliedRef.current = true;
    }
  }, [searchParams]);

  // Load customers, vorgaenge and reports
  const loadData = () => {
    Promise.all([
      api.get<Customer[]>('/api/customers'),
      api.get<ApiReport[]>('/api/reports'),
      api.get<Record<number, 'pending' | 'completed'>>('/api/customer-invitations/my-statuses'),
      api.get<ApiVorgang[]>('/api/vorgaenge'),
    ]).then(([customersData, reportsData, statusMap, vorgaengeData]) => {
      const reportsByCustomer: Record<number, ApiReport[]> = {};
      for (const r of reportsData) {
        if (!reportsByCustomer[r.customerId]) reportsByCustomer[r.customerId] = [];
        reportsByCustomer[r.customerId].push(r);
      }

      const vorgaengeByCustomer: Record<number, number> = {};
      for (const v of vorgaengeData) {
        vorgaengeByCustomer[v.customerId] = (vorgaengeByCustomer[v.customerId] || 0) + 1;
      }

      const rows: CustomerRow[] = customersData.map((c) => {
        const customerReports = reportsByCustomer[c.id] || [];
        let status: 'Aktiv' | 'Inaktiv' | 'Neu';
        if (customerReports.length === 0) {
          status = 'Neu';
        } else if (customerReports.every(r => r.status === 'Abgeschlossen')) {
          status = 'Inaktiv';
        } else {
          status = 'Aktiv';
        }

        const ansprechpartner = [c.vorname, c.nachname].filter(Boolean).join(' ') || (c as any).contactPerson || '';

        return {
          id: c.id,
          name: c.name,
          ansprechpartner,
          ort: c.ort || '',
          website: c.website || '',
          logoUrl: (c as any).logoUrl || '',
          wirtschaftszweig: c.wirtschaftszweig || '',
          berichteCount: vorgaengeByCustomer[c.id] || 0,
          status,
          letzteAktualisierungDatum: (c as any).lastEventAt
            ? new Date((c as any).lastEventAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })
            : '—',
          letzteAktualisierungZeit: (c as any).lastEventAt
            ? new Date((c as any).lastEventAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
            : '',
          letzteAktualisierungRaw: (c as any).lastEventAt || '',
          lastEventLabel: EVENT_SHORT[(c as any).lastEventTyp] ?? '',
          lastEventVorgangsnummer: (c as any).lastEventVorgangsnummer || '',
          offenerVorgang: (c as any).offenerVorgang ?? null,
          lastAntragDatum: (c as any).lastAntragAt
            ? new Date((c as any).lastAntragAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })
            : '',
          antragCountThisYear: (c as any).antragCountThisYear ?? 0,
          invitationStatus: statusMap[c.id] ?? null,
          fehlendeFelder: fehlendeLabels(c as any),
          vnReady: (c as any).vnReady ?? false,
          hasDocuments: (c as any).hasDocuments ?? false,
          foerderfaehigkeitAmpel: (c as any).foerderfaehigkeitAmpel ?? null,
          archived: (c as any).archived ?? false,
        };
      });
      lastStatusMapRef.current = statusMap;
      setCustomers(rows);
      setFullCustomers(customersData);
    }).catch(() => {});
  };

  useEffect(() => {
    loadData();
  }, []);

  // SSE: react immediately when a customer submits their form
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    const es = new EventSource(`${API_BASE}/api/customer-invitations/events?token=${encodeURIComponent(token)}`);
    es.addEventListener('form-submitted', () => {
      loadData();
      setNewDataNotice('Neue Kundendaten eingegangen!');
      setTimeout(() => setNewDataNotice(null), 5000);
    });
    return () => es.close();
  }, []);

  // Filter
  const filtered = customers.filter((c) => {
    if (c.archived && !showArchived) return false;
    const q = searchQuery.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      c.ansprechpartner.toLowerCase().includes(q) ||
      c.ort.toLowerCase().includes(q) ||
      c.wirtschaftszweig.toLowerCase().includes(q) ||
      c.status.toLowerCase().includes(q)
    );
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    if (!sortColumn) return 0;
    const dir = sortDirection === 'asc' ? 1 : -1;
    // „Letzte Änderung" chronologisch über das Roh-Datum sortieren (nicht den Anzeigestring).
    const key = sortColumn === 'letzteAktualisierung' ? 'letzteAktualisierungRaw' : sortColumn;
    const aVal = (a as any)[key];
    const bVal = (b as any)[key];
    if (typeof aVal === 'number' && typeof bVal === 'number') return dir * (aVal - bVal);
    return dir * String(aVal || '').localeCompare(String(bVal || ''), 'de');
  });

  const baseDisplayed = sorted.slice(0, rowsPerPage);
  // Deep-Link-Ziel garantiert anzeigen: liegt der aufgeklappte Kunde außerhalb der
  // rowsPerPage-Grenze, oben einreihen — aber nur solange er der aufgeklappte ist.
  const displayed = (deepLinkCustomerId != null && expandedId === deepLinkCustomerId
    && !baseDisplayed.some(c => c.id === deepLinkCustomerId))
    ? (() => { const t = sorted.find(c => c.id === deepLinkCustomerId); return t ? [t, ...baseDisplayed] : baseDisplayed; })()
    : baseDisplayed;

  // Zur aufgeklappten Zeile hinscrollen, sobald sie gerendert ist (z.B. Dashboard-Deep-Link
  // auf einen Kunden weiter unten in der Liste). Einmalig, damit späteres eigenes Scrollen
  // nicht unterbrochen wird.
  const customerScrollDoneRef = useRef(false);
  useEffect(() => {
    if (deepLinkCustomerId == null || customerScrollDoneRef.current) return;
    const el = document.getElementById(`customer-row-${deepLinkCustomerId}`);
    if (!el) return;
    customerScrollDoneRef.current = true;
    const timer = window.setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [deepLinkCustomerId, displayed]);

  // Grid-Template für die Kundentabelle — folgt der vom User festgelegten
  // Spaltenreihenfolge. Header und Datenzeilen erben via `subgrid`.
  const gridCols = useMemo(() => {
    const parts: string[] = ['1rem']; // Chevron (w-4) — fest
    const ortIsFlex = !visibleColumns.wirtschaftszweig;
    for (const key of columnOrder) {
      if (!isColumnVisible(key)) continue;
      parts.push(columnWidth(key, ortIsFlex));
    }
    parts.push('auto', 'auto', 'auto'); // Antrag-Button, Verwendungsnachweis-Button, 3-Punkte-Menü
    return parts.join(' ');
  }, [visibleColumns, columnOrder, narrowDesktop]);

  const toggleColumn = (col: string) => {
    setVisibleColumns(prev => ({ ...prev, [col]: !(prev as any)[col] }));
  };

  const toggleAllColumns = () => {
    const allVisible = Object.values(visibleColumns).every(v => v);
    const newState: any = {};
    for (const key of Object.keys(visibleColumns)) newState[key] = !allVisible;
    setVisibleColumns(newState);
  };

  const allColumnsVisible = Object.values(visibleColumns).every(v => v);

  const handleDelete = (id: number) => {
    api.delete(`/api/customers/${id}`).then(() => {
      setCustomers(prev => prev.filter(c => c.id !== id));
      setDeleteConfirmId(null);
    }).catch(() => {});
  };

  /** Archivieren blendet nur aus (Dashboard/Aufgaben) — im Unterschied zu handleDelete
   *  bleiben Kunde und Daten ohne Löschfrist erhalten. */
  const handleToggleArchived = (id: number, next: boolean) => {
    setCustomers(prev => prev.map(c => (c.id === id ? { ...c, archived: next } : c)));
    api.patch(`/api/customers/${id}`, { archived: next }).catch(() => {
      setCustomers(prev => prev.map(c => (c.id === id ? { ...c, archived: !next } : c)));
    });
  };

  const openAntragFor = (customerId: number) => {
    const full = fullCustomers.find(c => c.id === customerId);
    if (!full) return;
    sessionStorage.setItem('antragCustomer', JSON.stringify(full));
    // Zeitanker: Die spätere BAFA-Eingangsbestätigung wird per PLZ-Probe diesem Kunden zugeordnet.
    api.post('/api/customer-events/antrag-gestartet', { customerId }).catch(() => {});
    window.open('/antrag', '_blank');
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'Aktiv':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
      case 'Inaktiv':
        return 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300';
      case 'Neu':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
      default:
        return 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300';
    }
  };

  const colKeys = Object.keys(visibleColumns) as (keyof typeof visibleColumns)[];

  return (
    <div className="min-h-screen flex bg-slate-100 dark:bg-slate-900 overflow-x-hidden">
      {newDataNotice && (
        <div className="fixed top-4 right-4 z-[9999] flex items-center gap-2 px-4 py-3 bg-green-600 text-white text-sm font-medium rounded-xl shadow-lg animate-in fade-in slide-in-from-top-2">
          <Mail className="w-4 h-4" />
          {newDataNotice}
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-8">
          <div className="w-full max-w-full">

            {/* Kopfzeile — flach, damit mehr von der Liste auf den Schirm passt. */}
            <div className="mb-4">
              <CustomerManagement
                darkMode={darkMode}
                isExpanded={true}
                flach
                werkzeuge={(
                  <div className="flex items-center gap-2 justify-end">
                    {/* Mobil: ein Knopf für alles — dort fehlt die Kopfzeile zum Sortieren. */}
                    <button
                      onClick={() => setFilterSheetOpen(true)}
                      title="Filter"
                      aria-label="Filter"
                      className="md:hidden w-12 h-12 aspect-square flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors shadow-sm hover:shadow-md cursor-pointer relative"
                    >
                      <SlidersHorizontal className="w-5 h-5" />
                      {searchQuery.trim() !== '' && (
                        <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-blue-500" aria-hidden="true" />
                      )}
                    </button>
                    <div className="hidden md:flex items-center gap-2 flex-1 min-w-0">
                    <div className="relative flex-1 min-w-0">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        translate="no"
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Name, Ansprechperson, Ort, Branche..."
                        aria-label="Kunden durchsuchen"
                        className="w-full h-12 pl-9 pr-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-slate-400 dark:placeholder:text-slate-500"
                      />
                    </div>
                    <label className="hidden lg:flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 shrink-0 whitespace-nowrap cursor-pointer px-1">
                      <input type="checkbox" checked={showArchived} onChange={() => setShowArchived(v => !v)} className="w-3.5 h-3.5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" />
                      Archivierte
                    </label>
                    <div className="relative shrink-0">
                      <button
                        onClick={() => { setColumnsDropdownOpen(!columnsDropdownOpen); setRowsDropdownOpen(false); }}
                        title="Spalten wählen und sortieren"
                        aria-label="Spalten wählen und sortieren"
                        className={`w-12 h-12 aspect-square flex items-center justify-center rounded-lg transition-colors cursor-pointer shadow-sm hover:shadow-md ${
                          columnsDropdownOpen
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                        }`}
                      >
                        <Columns3 className="w-5 h-5" />
                      </button>
                      {columnsDropdownOpen && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setColumnsDropdownOpen(false)} />
                          <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-20 max-h-[28rem] overflow-y-auto">
                            <div className="p-2">
                              <label className="flex items-center gap-2 px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-600 rounded cursor-pointer border-b border-slate-200 dark:border-slate-600 mb-1">
                                <input type="checkbox" checked={allColumnsVisible} onChange={toggleAllColumns} className="w-4 h-4 text-blue-600 cursor-pointer" />
                                <span className="text-sm font-medium text-slate-900 dark:text-white">Alle Spalten</span>
                              </label>
                              <div className="text-[12px] uppercase tracking-wider text-slate-400 dark:text-slate-500 px-3 pt-1 pb-0.5">Ziehen zum Sortieren</div>
                              {columnOrder.map((col) => (
                                <div key={col}>
                                  <div
                                    draggable
                                    onDragStart={() => setDragKey(col)}
                                    onDragEnd={() => setDragKey(null)}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => { e.preventDefault(); if (dragKey) reorderTo(dragKey, col); setDragKey(null); }}
                                    className={`flex items-center gap-2 px-3 py-2 rounded select-none ${dragKey === col ? 'opacity-50' : ''} ${dragKey && dragKey !== col ? 'hover:bg-blue-50 dark:hover:bg-blue-900/30 border border-dashed border-transparent hover:border-blue-400' : 'hover:bg-slate-50 dark:hover:bg-slate-600'}`}
                                  >
                                    <input type="checkbox" checked={!!visibleColumns[col]} onChange={() => toggleColumn(col)} className="w-4 h-4 text-blue-600 cursor-pointer" onClick={(e) => e.stopPropagation()} />
                                    <span className="text-sm text-slate-700 dark:text-slate-300 flex-1">{columnLabels[col]}</span>
                                    <span className="cursor-grab active:cursor-grabbing text-slate-400" aria-hidden="true" title="Zum Verschieben ziehen">⋮⋮</span>
                                  </div>
                                  {col === 'name' && (
                                    <div className="ml-6 border-l border-slate-200 dark:border-slate-600 pl-2">
                                      <label className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-600">
                                        <input type="checkbox" checked={!!visibleColumns.logo} onChange={() => toggleColumn('logo')} className="w-4 h-4 text-blue-600 cursor-pointer" />
                                        <span className="text-sm text-slate-600 dark:text-slate-400">{columnLabels.logo}</span>
                                      </label>
                                      <label className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-600">
                                        <input type="checkbox" checked={!!visibleColumns.ansprechpartner} onChange={() => toggleColumn('ansprechpartner')} className="w-4 h-4 text-blue-600 cursor-pointer" />
                                        <span className="text-sm text-slate-600 dark:text-slate-400">{columnLabels.ansprechpartner}</span>
                                      </label>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <HilfeLink anleitungId="kunden-anlegen" variant="toolbar" label="Anleitung: Kunden anlegen" className="shrink-0" />
                    </div>
                  </div>
                )}
                onToggleExpanded={() => {}}
                onAddNew={() => {
                  setTriggerNewCustomer(prev => prev + 1);
                }}
                onRequestData={() => setDataRequestCustomer({ id: 0, email: '' })}
                hideManageButton
              />
            </div>

            {/* Hidden CustomerListCard - only for the modal functionality */}
            <div className="h-0 overflow-hidden">
              <CustomerListCard
                darkMode={darkMode}
                isExpanded={true}
                triggerNewCustomer={triggerNewCustomer}
                triggerEditCustomer={triggerEditCustomer}
                onChanged={loadData}
              />
            </div>

            {/* Customer Table */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
              {/* Card list */}
              {displayed.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
                  Keine Kunden gefunden.
                </div>
              ) : (
                <>
                {/* Mobile-Kartenliste (< md) — ab md übernimmt die Tabelle, die sich zwischen
                    768-1069px selbst schmaler macht (narrowDesktop, s.o.).
                    Nutzt dasselbe openMenuAt-Portal-Menü und CustomerDetailPanel wie die Tabelle. */}
                <ul className="md:hidden divide-y divide-slate-200 dark:divide-slate-700 border-t border-slate-200 dark:border-slate-700">
                  {displayed.map((row) => (
                    <Fragment key={row.id}>
                    <li
                      id={`customer-row-${row.id}`}
                      onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                      className="group flex items-start gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors cursor-pointer"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 min-w-0">
                          {visibleColumns.logo && <CustomerLogo website={row.website} logoUrl={row.logoUrl} />}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1 min-w-0">
                              <h3 translate="no" className="text-sm font-semibold text-slate-900 dark:text-white truncate">{row.name}</h3>
                              <button
                                onClick={(e) => { e.stopPropagation(); setTriggerEditCustomer({ id: row.id, ts: Date.now() }); }}
                                aria-label="Stammdaten bearbeiten"
                                title="Stammdaten bearbeiten"
                                className="p-1 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors shrink-0"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="flex items-center gap-2 min-w-0">
                              {row.ansprechpartner && (
                                <p translate="no" className="text-xs text-slate-500 dark:text-slate-400 truncate">{row.ansprechpartner}</p>
                              )}
                              <DatenStatus
                                fehlend={row.fehlendeFelder}
                                inv={row.invitationStatus}
                                onEdit={() => setTriggerEditCustomer({ id: row.id, ts: Date.now(), fokusLuecke: true })}
                              />
                            </div>
                          </div>
                        </div>
                        {!mediumMobile && (
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                            {row.ort && (
                              <span translate="no" className="inline-flex items-center gap-1 min-w-0 max-w-full">
                                <MapPin className="w-3 h-3 shrink-0" />
                                <span className="truncate">{row.ort}</span>
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1 shrink-0">
                              <FileText className="w-3.5 h-3.5" />
                              {row.berichteCount} {row.berichteCount === 1 ? 'Vorgang' : 'Vorgänge'}
                            </span>
                          </div>
                        )}
                        {mediumMobile ? (
                          <div className="mt-1.5">
                            <div className="flex items-center justify-between gap-2">
                              {row.lastAntragDatum ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[13px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                  {row.antragCountThisYear}/2 Anträge {currentYear}
                                </span>
                              ) : <span />}
                              <div className="flex items-center gap-2 shrink-0">
                                {row.vnReady && (
                                  <span className="flex items-center gap-1 px-2 py-1 text-[13px] font-medium rounded-lg bg-emerald-50 text-emerald-700 whitespace-nowrap select-none">
                                    <CheckCheck className="w-3.5 h-3.5 shrink-0" />
                                    VN
                                  </span>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const reasons = antragWarningReasons(row, currentYear);
                                    if (reasons.length > 0) { setAntragConfirm({ customerId: row.id, reasons }); return; }
                                    openAntragFor(row.id);
                                  }}
                                  disabled={row.foerderfaehigkeitAmpel === 'rot'}
                                  title={row.foerderfaehigkeitAmpel === 'rot' ? 'Nicht förderfähig — kein Antrag möglich' : antragWarningReasons(row, currentYear).join(' ') || undefined}
                                  className={`flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium rounded-lg transition-colors whitespace-nowrap ${row.foerderfaehigkeitAmpel === 'rot' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 cursor-not-allowed' : antragWarningReasons(row, currentYear).length > 0 ? 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300' : 'bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/50 text-purple-700 dark:text-purple-300'}`}
                                >
                                  <FileCheck className="w-3.5 h-3.5 shrink-0" />
                                  BEANTRAGEN
                                </button>
                              </div>
                            </div>
                            {row.lastAntragDatum && (
                              <span translate="no" className="block mt-1 text-[13px] text-slate-500 dark:text-slate-400">Letzte: {row.lastAntragDatum}</span>
                            )}
                          </div>
                        ) : (
                          <>
                            {row.lastAntragDatum && (
                              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[13px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                  {row.antragCountThisYear}/2 Anträge {currentYear}
                                </span>
                                <span translate="no" className="text-[13px] text-slate-500 dark:text-slate-400">Letzte: {row.lastAntragDatum}</span>
                              </div>
                            )}
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const reasons = antragWarningReasons(row, currentYear);
                                  if (reasons.length > 0) { setAntragConfirm({ customerId: row.id, reasons }); return; }
                                  openAntragFor(row.id);
                                }}
                                disabled={row.foerderfaehigkeitAmpel === 'rot'}
                                title={row.foerderfaehigkeitAmpel === 'rot' ? 'Nicht förderfähig — kein Antrag möglich' : antragWarningReasons(row, currentYear).join(' ') || undefined}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium rounded-lg transition-colors whitespace-nowrap ${row.foerderfaehigkeitAmpel === 'rot' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 cursor-not-allowed' : antragWarningReasons(row, currentYear).length > 0 ? 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300' : 'bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/50 text-purple-700 dark:text-purple-300'}`}
                              >
                                <FileCheck className="w-3.5 h-3.5 shrink-0" />
                                ANTRAG STELLEN
                              </button>
                              {row.vnReady && (
                                <span className="flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] font-medium rounded-lg bg-emerald-50 text-emerald-700 whitespace-nowrap select-none">
                                  <CheckCheck className="w-3.5 h-3.5 shrink-0" />
                                  VN bereit
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      {mediumMobile ? (
                        <div className="flex flex-col items-end gap-1 shrink-0 text-right">
                          {row.ort && (
                            <span translate="no" className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 max-w-[8rem]">
                              <MapPin className="w-3 h-3 shrink-0" />
                              <span className="truncate">{row.ort}</span>
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                            <FileText className="w-3.5 h-3.5 shrink-0" />
                            {row.berichteCount} Vorg.
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); openMenuAt(row.id, e); }}
                            aria-label="Aktionen"
                            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center shrink-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); openMenuAt(row.id, e); }}
                            aria-label="Aktionen"
                            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </li>
                    {expandedId === row.id && (
                      <li className="px-4 py-4 bg-slate-100 dark:bg-slate-900/40">
                        {row.fehlendeFelder.length > 0 && (
                          <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-[14px] text-amber-800 dark:text-amber-300">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                            <span className="font-medium">Fehlende Pflichtfelder:</span>
                            <span className="min-w-0">{fehlendText(row.fehlendeFelder)}</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); setTriggerEditCustomer({ id: row.id, ts: Date.now(), fokusLuecke: true }); }}
                              className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-100 dark:bg-amber-900/40 hover:bg-amber-200 dark:hover:bg-amber-900/60 font-medium transition-colors"
                            >
                              <Pencil className="w-3 h-3" />
                              Bearbeiten
                            </button>
                          </div>
                        )}
                        <CustomerDetailPanel customerId={row.id} />
                      </li>
                    )}
                    </Fragment>
                  ))}
                </ul>
                {/* Desktop-Tabelle (≥ md) — bleibt gemountet (hidden), damit das Portal-Menü auch aus den Karten
                    funktioniert. overflow-x-auto bleibt als Sicherheitsnetz für Restfälle. */}
                <div className="hidden md:block overflow-x-auto rounded-t-xl">
                <ul
                  style={{ gridTemplateColumns: gridCols }}
                  className="grid gap-x-4 sm:gap-x-6 divide-y divide-slate-200 dark:divide-slate-700 min-w-fit"
                >
                  {/* Spalten-Header — nutzt subgrid → erbt die Spalten vom <ul>.
                      Damit sind Header und Datenzeilen pixelgenau zueinander aligned. */}
                  <li className="col-span-full grid grid-cols-subgrid items-center px-3 sm:px-4 py-2.5 bg-white dark:bg-slate-800 text-[13px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 select-none">
                    <span aria-hidden="true" />
                    {columnOrder.filter(k => isColumnVisible(k)).map(key => {
                      const meta: Record<string, { label: string; align: string; title?: string }> = {
                        name: { label: 'Name', align: 'text-left' },
                        ort: { label: narrowDesktop ? 'Ort / Vorgänge' : 'Ort', align: 'text-left' },
                        wirtschaftszweig: { label: 'Branche', align: 'text-left' },
                        berichteCount: { label: 'Vorgänge', align: 'text-center' },
                        status: { label: 'Status', align: 'text-center' },
                        letzteAktualisierung: { label: 'Letzte Änderung', align: 'text-center' },
                      };
                      const m = meta[key];
                      if (!m) return null;
                      // Sortiert wird hier, wo die Spalte steht: Klick wechselt die Richtung,
                      // die zuletzt geklickte Spalte bestimmt die Sortierung.
                      const aktiv = sortColumn === key;
                      const ausrichtung = m.align === 'text-center' ? 'justify-center' : 'text-left';
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setSort(key)}
                          title={`Nach ${m.label} sortieren`}
                          className={`group flex items-center gap-1 min-w-0 uppercase tracking-wider font-semibold text-[13px] cursor-pointer transition-colors ${ausrichtung} ${
                            aktiv ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                          }`}
                        >
                          <span className="truncate">{m.label}</span>
                          {/* Beide Pfeile stehen immer da — sie zeigen, dass man hier sortieren kann.
                              Blau ist nur der Pfeil der gerade gewählten Richtung. */}
                          <span className="shrink-0 flex flex-col leading-none -space-y-1" aria-hidden="true">
                            <ArrowUp className={`w-2.5 h-2.5 ${aktiv && sortDirection === 'asc' ? 'text-blue-600 dark:text-blue-400' : 'text-slate-300 dark:text-slate-600'}`} />
                            <ArrowDown className={`w-2.5 h-2.5 ${aktiv && sortDirection === 'desc' ? 'text-blue-600 dark:text-blue-400' : 'text-slate-300 dark:text-slate-600'}`} />
                          </span>
                        </button>
                      );
                    })}
                    {/* Spaltenplätze für Antrag-Button, Verwendungsnachweis-Button und 3-Punkte-Menü — leer im Header */}
                    <span aria-hidden="true" />
                    <span aria-hidden="true" />
                    <span aria-hidden="true" />
                  </li>
                  {displayed.map((row) => (
                    <Fragment key={row.id}>
                    <li
                      id={`customer-row-${row.id}`}
                      onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                      className="group col-span-full grid grid-cols-subgrid items-center px-3 sm:px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors cursor-pointer last:rounded-b-xl"
                    >
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedId === row.id ? '' : '-rotate-90'}`} />
                      {columnOrder.filter(k => isColumnVisible(k)).map(key => {
                        switch (key) {
                          case 'name':
                            return (
                              <div key={key} className="min-w-0 flex items-center gap-3 text-left">
                                {!veryNarrowDesktop && visibleColumns.logo && <CustomerLogo website={row.website} logoUrl={row.logoUrl} />}
                                <div className="min-w-0">
                                  <div className="relative flex items-center gap-1 min-w-0 min-h-[1.75rem]">
                                    <h3 translate="no" className="text-sm font-semibold text-slate-900 dark:text-white truncate">{row.name}</h3>
                                    {/* Solange der Knopf unsichtbar ist, liegt er absolut und belegt keine
                                        Breite — der Firmenname bekommt die ganze Spalte. Beim Überfahren
                                        (oder bei aufgeklappter Zeile) rückt er in den Fluss, und der Name
                                        kürzt sich mit Auslassungspunkten. */}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setTriggerEditCustomer({ id: row.id, ts: Date.now() }); }}
                                      aria-label="Stammdaten bearbeiten"
                                      title="Stammdaten bearbeiten"
                                      className={`inline-flex items-center gap-1 px-1.5 py-1 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-opacity shrink-0 text-[13px] font-medium ${
                                        expandedId === row.id
                                          ? 'static opacity-100'
                                          : 'absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:static group-hover:translate-y-0 group-hover:opacity-100 focus-visible:static focus-visible:translate-y-0 focus-visible:opacity-100'
                                      }`}
                                    >
                                      <Pencil className="w-3 h-3 shrink-0" />
                                      {!veryNarrowDesktop && <span>Bearbeiten</span>}
                                    </button>
                                  </div>
                                  <div className="flex items-center gap-2 min-w-0 mt-0.5">
                                    {visibleColumns.ansprechpartner && row.ansprechpartner && (
                                      <p translate="no" className="text-xs text-slate-500 dark:text-slate-400 truncate">{row.ansprechpartner}</p>
                                    )}
                                    <DatenStatus
                                      fehlend={row.fehlendeFelder}
                                      inv={row.invitationStatus}
                                      onEdit={() => setTriggerEditCustomer({ id: row.id, ts: Date.now(), fokusLuecke: true })}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          case 'ort':
                            return (
                              <div key={key} className="min-w-0">
                                <span translate="no" className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1 truncate max-w-full">
                                  <MapPin className="w-3 h-3 shrink-0" />
                                  <span className="truncate">{row.ort || '—'}</span>
                                </span>
                                {narrowDesktop && (
                                  <span className="mt-0.5 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                                    <FileText className="w-3 h-3 shrink-0" />
                                    {row.berichteCount}
                                  </span>
                                )}
                              </div>
                            );
                          case 'wirtschaftszweig':
                            return (
                              <div key={key} className="min-w-0 text-left">
                                <span translate="no" className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 leading-tight" title={row.wirtschaftszweig}>
                                  {row.wirtschaftszweig || '—'}
                                </span>
                              </div>
                            );
                          case 'berichteCount':
                            return (
                              <div key={key} className="flex items-center justify-center">
                                <span className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
                                  <FileText className="w-3.5 h-3.5" />
                                  {row.berichteCount}
                                </span>
                              </div>
                            );
                          case 'status':
                            return (
                              <div key={key} className="flex flex-col items-center gap-1 leading-tight">
                                {row.lastAntragDatum ? (
                                  <>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[13px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                      {row.antragCountThisYear}/2 Anträge {currentYear}
                                    </span>
                                    <span translate="no" className="text-[13px] text-slate-500 dark:text-slate-400">Letzte: {row.lastAntragDatum}</span>
                                  </>
                                ) : (
                                  <span className="text-[13px] text-slate-400 dark:text-slate-500">—</span>
                                )}
                              </div>
                            );
                          case 'letzteAktualisierung':
                            return (
                              <div key={key} className="text-center">
                                <div translate="no" className="text-xs text-slate-500 dark:text-slate-400 leading-tight">{row.letzteAktualisierungDatum}</div>
                                {row.letzteAktualisierungZeit && (
                                  <div translate="no" className="text-[13px] text-slate-400 dark:text-slate-500 leading-tight">{row.letzteAktualisierungZeit} Uhr</div>
                                )}
                              </div>
                            );
                          default:
                            return null;
                        }
                      })}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const reasons = antragWarningReasons(row, currentYear);
                          if (reasons.length > 0) { setAntragConfirm({ customerId: row.id, reasons }); return; }
                          openAntragFor(row.id);
                        }}
                        disabled={row.foerderfaehigkeitAmpel === 'rot'}
                        title={row.foerderfaehigkeitAmpel === 'rot' ? 'Nicht förderfähig — kein Antrag möglich' : antragWarningReasons(row, currentYear).join(' ') || undefined}
                        className={`flex items-center gap-1.5 px-4 py-1.5 text-[13px] font-medium rounded-lg transition-colors whitespace-nowrap ${row.foerderfaehigkeitAmpel === 'rot' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 cursor-not-allowed' : antragWarningReasons(row, currentYear).length > 0 ? 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300' : 'bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/50 text-purple-700 dark:text-purple-300'}`}
                      >
                        <FileCheck className="w-3.5 h-3.5 shrink-0" />
                        {veryNarrowDesktop ? 'BEANTRAGEN' : 'ANTRAG STELLEN'}
                      </button>
                      {row.vnReady && (
                        <span className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium rounded-lg bg-emerald-50 text-emerald-700 whitespace-nowrap select-none">
                          <CheckCheck className="w-3.5 h-3.5 shrink-0" />
                          VN bereit
                        </span>
                      )}
                      <div className="relative">
                        <button
                          onClick={(e) => { e.stopPropagation(); openMenuAt(row.id, e); }}
                          onMouseLeave={scheduleMenuClose}
                          onMouseEnter={cancelMenuClose}
                          aria-label="Aktionen"
                          className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        {openMenuId === row.id && menuPos && createPortal(
                          <>
                            <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); }} />
                            <div
                              style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
                              onMouseEnter={cancelMenuClose}
                              onMouseLeave={scheduleMenuClose}
                              className="w-48 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-50 py-1 whitespace-nowrap"
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuId(null);
                                  setTriggerEditCustomer({ id: row.id, ts: Date.now() });
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                                Bearbeiten
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuId(null);
                                  const full = fullCustomers.find(c => c.id === row.id);
                                  setDataRequestCustomer({ id: row.id, email: full?.email || '' });
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                              >
                                <Mail className="w-3.5 h-3.5" />
                                Daten anfordern
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuId(null);
                                  handleToggleArchived(row.id, !row.archived);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                              >
                                {row.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                                {row.archived ? 'Wiederherstellen' : 'Archivieren'}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuId(null);
                                  setDeleteConfirmId(row.id);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-slate-600 transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                L&ouml;schen
                              </button>
                            </div>
                          </>,
                          document.body
                        )}
                      </div>
                    </li>
                    {expandedId === row.id && (
                      <li className="col-span-full px-3 sm:px-4 py-4 bg-slate-100 dark:bg-slate-900/40">
                        {row.fehlendeFelder.length > 0 && (
                          <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-[14px] text-amber-800 dark:text-amber-300">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                            <span className="font-medium">Fehlende Pflichtfelder:</span>
                            <span className="min-w-0">{fehlendText(row.fehlendeFelder)}</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); setTriggerEditCustomer({ id: row.id, ts: Date.now(), fokusLuecke: true }); }}
                              className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-100 dark:bg-amber-900/40 hover:bg-amber-200 dark:hover:bg-amber-900/60 font-medium transition-colors"
                            >
                              <Pencil className="w-3 h-3" />
                              Bearbeiten
                            </button>
                          </div>
                        )}
                        <CustomerDetailPanel customerId={row.id} />
                      </li>
                    )}
                    </Fragment>
                  ))}
                </ul>
                </div>
                </>
              )}

              {/* Fußzeile: Trefferzahl links, Seitengröße rechts — beides gehört zur Liste. */}
              <div className="px-3 sm:px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3">
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  {filtered.length} {filtered.length === 1 ? 'Kunde' : 'Kunden'} gefunden
                  {filtered.length > rowsPerPage && `, ${rowsPerPage} angezeigt`}
                </p>
                <div className="relative shrink-0">
                  <button
                    onClick={() => { setRowsDropdownOpen(!rowsDropdownOpen); setColumnsDropdownOpen(false); }}
                    title="Einträge pro Seite"
                    className="px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 flex items-center gap-1 cursor-pointer"
                  >
                    <span>{rowsPerPage} pro Seite</span>
                    <ChevronDown className="w-4 h-4 flex-shrink-0" />
                  </button>
                  {rowsDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setRowsDropdownOpen(false)} />
                      <div className="absolute right-0 bottom-full mb-2 min-w-[120px] bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-20">
                        <div className="p-1">
                          {[10, 25, 50].map((n) => (
                            <button key={n} onClick={() => { setRowsPerPage(n); setRowsDropdownOpen(false); }}
                              className={`w-full px-4 py-2 text-sm rounded text-center cursor-pointer ${n === rowsPerPage ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600'}`}
                            >{n}</button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Filter-Dialog (nur Mobil) — Vollbild, Inhalt scrollt, Kopf und Fuß stehen fest.
                Enthält Suche, Sortierung und Spalten als Ziehharmonika; die Seitengröße
                bleibt bewusst draußen in der Fußzeile der Liste. */}
            {filterSheetOpen && (
              <div className="md:hidden fixed inset-0 z-50 flex flex-col bg-white dark:bg-slate-800">
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white">Filter</h3>
                  <button onClick={() => setFilterSheetOpen(false)} aria-label="Schließen"
                    className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                  <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        translate="no"
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Name, Ansprechperson, Ort, Branche..."
                        aria-label="Kunden durchsuchen"
                        className="w-full h-11 pl-9 pr-9 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {searchQuery !== '' && (
                        <button onClick={() => setSearchQuery('')} aria-label="Suche zurücksetzen"
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {([
                    { key: 'sortierung' as const, label: 'Sortierung' },
                    { key: 'spalten' as const, label: 'Spalten' },
                  ]).map(bereich => {
                    const offen = offenerBereich === bereich.key;
                    return (
                      <div key={bereich.key} className="border-b border-slate-200 dark:border-slate-700">
                        <button
                          onClick={() => setOffenerBereich(offen ? null : bereich.key)}
                          className="w-full flex items-center justify-between gap-2 px-4 py-3 text-sm font-medium text-slate-900 dark:text-white"
                        >
                          <span className="flex items-center gap-2">
                            {bereich.label}
                            {bereich.key === 'sortierung' && (
                              <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                                {SORT_OPTIONS.find(o => o.key === sortColumn)?.label}
                              </span>
                            )}
                          </span>
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${offen ? '' : '-rotate-90'}`} />
                        </button>

                        {offen && bereich.key === 'sortierung' && (
                          <div className="px-2 pb-3">
                            {SORT_OPTIONS.map(o => {
                              const aktiv = o.key === sortColumn;
                              return (
                                <button
                                  key={o.key}
                                  onClick={() => setSort(o.key)}
                                  className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded text-sm text-left ${
                                    aktiv ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium' : 'text-slate-700 dark:text-slate-300'
                                  }`}
                                >
                                  <span>{o.label}</span>
                                  {aktiv && (sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />)}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {offen && bereich.key === 'spalten' && (
                          <div className="px-2 pb-3">
                            <label className="flex items-center gap-2 px-3 py-2.5 rounded">
                              <input type="checkbox" checked={allColumnsVisible} onChange={toggleAllColumns} className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-slate-900 dark:text-white">Alle Spalten</span>
                            </label>
                            {columnOrder.map(col => (
                              <div key={col}>
                                <label className="flex items-center gap-2 px-3 py-2 rounded">
                                  <input type="checkbox" checked={!!visibleColumns[col]} onChange={() => toggleColumn(col)} className="w-4 h-4 text-blue-600" />
                                  <span className="text-sm text-slate-700 dark:text-slate-300">{columnLabels[col]}</span>
                                </label>
                                {col === 'name' && (
                                  <div className="ml-6 border-l border-slate-200 dark:border-slate-600 pl-2">
                                    <label className="flex items-center gap-2 px-2 py-1.5 rounded">
                                      <input type="checkbox" checked={!!visibleColumns.logo} onChange={() => toggleColumn('logo')} className="w-4 h-4 text-blue-600" />
                                      <span className="text-sm text-slate-600 dark:text-slate-400">{columnLabels.logo}</span>
                                    </label>
                                    <label className="flex items-center gap-2 px-2 py-1.5 rounded">
                                      <input type="checkbox" checked={!!visibleColumns.ansprechpartner} onChange={() => toggleColumn('ansprechpartner')} className="w-4 h-4 text-blue-600" />
                                      <span className="text-sm text-slate-600 dark:text-slate-400">{columnLabels.ansprechpartner}</span>
                                    </label>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 shrink-0">
                  <button
                    onClick={() => setFilterSheetOpen(false)}
                    className="w-full px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
                  >
                    {filtered.length} {filtered.length === 1 ? 'Kunde' : 'Kunden'} anzeigen
                  </button>
                </div>
              </div>
            )}

            {/* Delete Confirmation Dialog */}
            {deleteConfirmId !== null && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl p-6 max-w-sm mx-4">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Kunde l&ouml;schen?</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                    M&ouml;chten Sie den Kunden &bdquo;<span translate="no">{customers.find(c => c.id === deleteConfirmId)?.name}</span>&ldquo; wirklich l&ouml;schen? Diese Aktion kann nicht r&uuml;ckg&auml;ngig gemacht werden.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setDeleteConfirmId(null)}
                      className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors font-medium cursor-pointer"
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={() => handleDelete(deleteConfirmId)}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium cursor-pointer"
                    >
                      L&ouml;schen
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Antrag-Rückfrage (Jahreslimit erreicht / Dokumente fehlen).
                Mobile: Bottom-Sheet über volle Breite (leicht per Daumen erreichbar).
                Ab sm: fest zentrierte Karte mit fixer Breite (nicht content-abhängig,
                sonst variiert die Breite/Button-Größe je nach Länge der Gründe). */}
            {antragConfirm && (
              <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 animate-in fade-in duration-150">
                <div className="w-full sm:max-w-sm sm:mx-4 bg-white dark:bg-slate-800 sm:border sm:border-slate-200 dark:sm:border-slate-700 shadow-xl p-6 rounded-t-2xl sm:rounded-xl animate-in fade-in slide-in-from-bottom-4 sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-200">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Wirklich einen Antrag stellen?</h3>
                  <ul className="text-sm text-slate-600 dark:text-slate-400 mb-6 list-disc pl-5 space-y-1">
                    {antragConfirm.reasons.map((reason, i) => <li key={i}>{reason}</li>)}
                  </ul>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => setAntragConfirm(null)}
                      className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors font-medium cursor-pointer"
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={() => { openAntragFor(antragConfirm.customerId); setAntragConfirm(null); }}
                      className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium cursor-pointer"
                    >
                      Trotzdem stellen
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {dataRequestCustomer && (
        <DataRequestModal
          customerId={dataRequestCustomer.id || undefined}
          prefillEmail={dataRequestCustomer.email}
          onClose={() => setDataRequestCustomer(null)}
          onSuccess={loadData}
        />
      )}
    </div>

  );
}
